import { get, post } from '../lib/ajax.js'

class Cart {
  /**
   * Handles errors for Shopify's add/change endpoints, and alert the user an error occurred
   * @param {object} e - API http/network request error object
   */
  handleCartError(e) {
    var errorMsg = 'An error occurred while trying to add/change quantity of cart item.'

    if (e.status && e.status === 422) {
      errorMsg = `${e.message}: ${e.description}`
    }
    console.log('Handle Cart Error', e)
    alert(errorMsg)
  }

  /**
   * Add an item to cart using variantId and quantity
   * @param {integer} quantity
   * @param {integer} variantId
   * @returns {object} returns the product line item data of the product added to cart
   */
  add(quantity, variantId, properties = null) {
    return new Promise((resolve) => {
      //shopify has to have the X-requested-with header to be able to return 422 only on /add.js endpoint
      //https://community.shopify.com/c/Shopify-Design/AJAX-POST-cart-add-js-NEVER-returns-422-only-200-OK-on/td-p/375736
      var headers = {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      }

      const data = { quantity: quantity, id: variantId }
      if (properties) {
        data.properties = properties
      }
      post('/cart/add.js', data, headers)
        .then((res) => {
          resolve(res)
        })
        .catch((e) => {
          this.handleCartError(e)
        })
    })
  }

  /**
   * Change a cart line item using the line item key and the quantity you want to change it to.
   * @param {integer} quantity
   * @param {string} key 32 character long alphanumeric string
   * @returns {object} returns new cart data reflecting the change
   */
  change(quantity, key) {
    var data = { id: key, quantity: quantity }
    return new Promise((resolve, reject) => {
      post('/cart/change.js', data)
        .then((res) => {
          resolve(res)
        })
        .catch((e) => {
          this.handleCartError(e)
        })
    })
  }
  /**
   *  Fetches cart data and joins other product data into one object
   * @returns {object} returns cart data in addition to some product data Shopify doesn't provide
   */
  fetchNewCart() {
    return new Promise((resolve, reject) => {
      get('/cart.js')
        .then(async (res) => {
          var cartMeta = await get('/cart?view=variant-meta')
          var cart = res
          var totalCompareSavings = 0
          var promises = []
          this.count = cart.item_count
          cart['has_subscription'] = false

          cart.items.forEach((cartItem) => {
            const variantMeta = cartMeta?.find((el) => parseInt(el.id) === cartItem.variant_id)
            const isSubscription = cartItem?.properties?.is_subscription ? true : false
            const handle = cartItem?.properties?.subscription_id ? cartItem?.properties?.handle : cartItem.handle
            const variantId = cartItem?.properties?.original_variant_id ?? cartItem.variant_id
            cartItem['pack_size'] = variantMeta?.pack_size
            cartItem['pack_size_unit'] = variantMeta?.pack_size_unit
            cartItem['is_subscription'] = isSubscription

            promises.push(
              get(`/products/${handle}.js`)
                .then((product) => {
                  const index = product.variants.findIndex((el) => el.id === variantId)
                  const variant = product?.variants[index]
                  const type = product.type.toLowerCase()
                  cartItem['compare_at_price'] = variant?.compare_at_price
                  cartItem['product_tags'] = product?.tags
                  cartItem['type'] = type

                  if (isSubscription) {
                    cart['has_subscription'] = true
                    cartItem['subscription_product_title'] = cartItem['product_title']
                    cartItem['subscription_title'] = cartItem['title']
                    cartItem['title'] = product.title
                    cartItem['product_title'] = product.title
                    cartItem['url'] = product.url + '?variant=' + variantId
                  }

                  if (
                    variant?.compare_at_price &&
                    variant?.compare_at_price > variant?.price &&
                    cartItem.discounted_price === variant.price
                  ) {
                    const productSavings = (variant.compare_at_price - variant.price) * cartItem.quantity
                    totalCompareSavings += productSavings
                  }
                })
                .catch((e) => {
                  console.log('An error occurred in getting products in cart.js.', e)
                  reject(e)
                })
            )
          })

          Promise.all(promises).then(() => {
            cart['total_compare_at_savings'] = totalCompareSavings
            cart['total_savings'] = res.original_total_price - res.total_price + totalCompareSavings
            resolve(cart)
          })
        })
        .catch((e) => {
          console.log(`Could't get cart data.`, e)
          alert('An error occurred while trying to get new cart data.')
        })
    })
  }

  /**
   * Clears the cart of all items
   * @returns {object} returns empty cart data
   */
  clear() {
    return new Promise((resolve) => {
      post('/cart/clear.js')
        .then((res) => {
          resolve(res)
        })
        .catch((e) => {
          console.log('An error occurred while trying to clear cart.', e)
          alert('Error occurred trying to clear cart.')
        })
    })
  }
}

export const shopifyCart = new Cart()

export { Cart }
